import { Avatar } from "@/app/nav";
import type { Person2 } from "@/app/peopleQueries";
import { SELECT_CLS, TONE_TEXT } from "./shared";
import { MAIN_STEPS, ROLE_RU, STATUS_TONE, cohortLabel, gatesLabel, type Role } from "./model";

const HEAD = ["", "человек", "email", "роль", "когорта", "ментор", "прогресс", "статус"];

const ROLE_BADGE: Record<Role, string> = {
  student: "bg-card2 text-ink2 border-bd2",
  mentor: "bg-accsoft text-acc border-acc",
  admin: "bg-infosoft text-info border-info",
  owner: "bg-transparent text-mut border-bd2",
};

const STATUS_BADGE: Record<Person2["status"], string> = {
  активен: "bg-oksoft text-ok border-ok",
  молчит: "bg-warnsoft text-warn border-warn",
  ожидает: "bg-transparent text-mut border-bd",
};

const ROLE_OPTS: Role[] = ["student", "mentor", "admin", "owner"];

export type TableProps = {
  rows: Person2[];
  mentors: Person2[];
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onToggleAll: (ids: string[], on: boolean) => void;
  onAssign: (studentId: string, mentorId: string | undefined) => void;
  onRole: (userId: string, role: Role) => void;
  /** Роль смотрящего: роли меняет админ и владелец; «владелец» — только владелец (сервер вернёт 403). */
  viewerRole: string;
  viewerId?: string;
  busy?: boolean;
};

/** Таблица людей: чекбокс, человек, email, роль (select для админа), когорта,
 *  ментор (select в строке студента), прогресс «N из 29», статус точкой и словом. */
export function PeopleTable({ rows, mentors, selected, onToggle, onToggleAll, onAssign, onRole, viewerRole, viewerId, busy }: TableProps) {
  const ids = rows.map((r) => r.id);
  const allOn = ids.length > 0 && ids.every((id) => selected.has(id));
  const canRoles = viewerRole === "admin" || viewerRole === "owner";
  return (
    <div className="overflow-x-auto" data-testid="people-table-wrap">
      <table className="w-full min-w-[920px] border-collapse" data-testid="people-table">
        <thead>
          <tr className="bg-bg2">
            {HEAD.map((h, i) => (
              <th key={i} scope="col" className="px-2.5 py-2.5 text-left font-mono text-[10.5px] font-normal uppercase tracking-[.06em] text-mut first:pl-5 last:pr-5">
                {i === 0 ? (
                  <input type="checkbox" aria-label="выбрать всех видимых" checked={allOn} onChange={(e) => onToggleAll(ids, e.target.checked)} className="size-[15px] accent-acc" disabled={ids.length === 0} />
                ) : (
                  h
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const on = selected.has(p.id);
            const student = p.role === "student";
            const pct = Math.round((p.gates_passed / MAIN_STEPS) * 100);
            return (
              <tr key={p.id} data-testid="person-row" data-id={p.id} data-role={p.role} aria-selected={on} className={`border-b border-bd align-middle ${on ? "bg-card" : "hover:bg-card2/40"}`}>
                <td className="py-2.5 pl-5 pr-1">
                  <input type="checkbox" aria-label={`выбрать ${p.name}`} checked={on} onChange={() => onToggle(p.id)} className="size-[15px] accent-acc" />
                </td>
                <td className="px-2.5 py-2.5">
                  <span className="flex items-center gap-2.5">
                    <Avatar name={p.name} />
                    <span className="truncate text-[13px] text-ink" data-testid="row-name">
                      {p.name}
                    </span>
                  </span>
                </td>
                <td className="px-2.5 py-2.5 font-mono text-[11.5px] text-mut">{p.email || "—"}</td>
                <td className="px-2.5 py-2.5">
                  {canRoles && p.id !== viewerId ? (
                    <select aria-label={`роль ${p.name}`} data-testid="row-role" value={p.role} disabled={busy} onChange={(e) => onRole(p.id, e.target.value as Role)} className={SELECT_CLS}>
                      {ROLE_OPTS.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_RU[r]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span data-testid="row-role" className={`inline-flex items-center rounded-sm border px-2 py-[2px] font-mono text-[10.5px] ${ROLE_BADGE[p.role]}`}>
                      {ROLE_RU[p.role]}
                    </span>
                  )}
                </td>
                <td className={`px-2.5 py-2.5 font-mono text-[11.5px] ${p.cohort ? "text-ink2" : "text-mut"}`} data-testid="row-cohort">
                  {cohortLabel(p)}
                </td>
                <td className="px-2.5 py-2.5">
                  {student ? (
                    <select aria-label={`ментор для ${p.name}`} data-testid="row-mentor" value={p.mentor_id ?? ""} disabled={busy} onChange={(e) => onAssign(p.id, e.target.value || undefined)} className={`${SELECT_CLS} ${p.mentor_id ? "" : "text-mut"}`}>
                      <option value="">{p.mentor_id ? "снять ментора" : "без ментора"}</option>
                      {mentors.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="font-mono text-[11.5px] text-mut">—</span>
                  )}
                </td>
                <td className="px-2.5 py-2.5" data-testid="row-gates">
                  {student ? (
                    <span className="flex flex-col gap-1">
                      <span className="font-mono text-[11.5px] text-ink2">{gatesLabel(p)}</span>
                      <span className="block h-[5px] w-[72px] overflow-hidden rounded-[2px] bg-card2" role="img" aria-label={`${p.gates_passed} из ${MAIN_STEPS}`}>
                        <span className="block h-full bg-acc" style={{ width: `${pct}%` }} />
                      </span>
                    </span>
                  ) : (
                    <span className="font-mono text-[11.5px] text-mut">—</span>
                  )}
                </td>
                <td className="py-2.5 pl-2.5 pr-5">
                  <span data-testid="row-status" data-status={p.status} className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-[3px] font-mono text-[10.5px] ${STATUS_BADGE[p.status]}`}>
                    <span aria-hidden className={`size-1.5 rounded-full bg-current ${TONE_TEXT[STATUS_TONE[p.status]]}`} />
                    {p.status}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
